import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BuildingOfficeIcon,
  EnvelopeIcon,
  PhoneIcon,
  ArrowRightIcon,
  BriefcaseIcon,
  CurrencyDollarIcon,
} from '@heroicons/react/24/outline';
import { Modal, Button, Card, CardBody, Spinner } from '../ui';

interface ClientPreviewModalProps {
  clientId: number;
  clientName: string;
  clientEmail?: string;
  clientPhone?: string[];
  isOpen: boolean;
  onClose: () => void;
}

interface ClientStats {
  activeDeals: number;
  totalDealsValue: number;
  activeTasks: number;
}

const ClientPreviewModal: React.FC<ClientPreviewModalProps> = ({
  clientId,
  clientName,
  clientEmail,
  clientPhone,
  isOpen,
  onClose,
}) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<ClientStats>({ activeDeals: 0, totalDealsValue: 0, activeTasks: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen) {
      loadClientStats();
    }
  }, [isOpen, clientId]);

  const loadClientStats = async () => {
    try {
      setLoading(true);
      // TODO: Replace with actual API call to get client stats
      // For now, using mock data
      setStats({
        activeDeals: 3,
        totalDealsValue: 45000,
        activeTasks: 7,
      });
    } catch (error) {
      console.error('Error loading client stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = () => {
    onClose();
    navigate(`/admin/clients/${clientId}`);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Client Overview" size="md">
      {/* Client Info */}
      <div className="flex items-center mb-6">
        <div className="flex-shrink-0 h-12 w-12 rounded-full bg-primary-100 flex items-center justify-center">
          <BuildingOfficeIcon className="h-6 w-6 text-primary-600" />
        </div>
        <div className="ml-4 flex-1">
          <h3 className="text-base font-medium text-neutral-900">{clientName}</h3>
          {clientEmail && (
            <div className="flex items-center text-sm text-neutral-500 mt-1">
              <EnvelopeIcon className="h-4 w-4 mr-1" />
              {clientEmail}
            </div>
          )}
          {clientPhone && clientPhone.length > 0 && (
            <div className="flex items-center text-sm text-neutral-500 mt-1">
              <PhoneIcon className="h-4 w-4 mr-1" />
              {clientPhone[0]}
            </div>
          )}
        </div>
      </div>

      {/* Stats */}
      {loading ? (
        <div className="flex justify-center py-4">
          <Spinner size="md" />
        </div>
      ) : (
        <div className="space-y-3 mb-6">
          <Card variant="bordered" className="bg-success-50">
            <CardBody className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <BriefcaseIcon className="h-5 w-5 text-success-600 mr-2" />
                  <span className="text-sm font-medium text-neutral-700">Active Deals</span>
                </div>
                <span className="text-lg font-bold text-success-600">{stats.activeDeals}</span>
              </div>
            </CardBody>
          </Card>
          <Card variant="bordered" className="bg-primary-50">
            <CardBody className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <CurrencyDollarIcon className="h-5 w-5 text-primary-600 mr-2" />
                  <span className="text-sm font-medium text-neutral-700">Total Deal Value</span>
                </div>
                <span className="text-lg font-bold text-primary-600">
                  ${stats.totalDealsValue.toLocaleString()}
                </span>
              </div>
            </CardBody>
          </Card>
        </div>
      )}

      {/* Action Button */}
      <Button
        variant="primary"
        onClick={handleViewDetails}
        className="w-full"
        rightIcon={<ArrowRightIcon className="h-4 w-4" />}
      >
        View Client Details
      </Button>
    </Modal>
  );
};

export default ClientPreviewModal;

